import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { $getNodeByKey, $getSelection, $isNodeSelection, CLICK_COMMAND, COMMAND_PRIORITY_LOW, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND } from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { $isBlockNode } from './BlockNode';
import IconV2 from '../v2/IconV2';
import workflowScreenSelectors from '../../screens/workflows/redux/selectors/workflowScreenSelectors';
import selectors from '../../redux/selectors/selectors';
import { FiStar } from '@/components/react-icons/main';
import { formatAttributeKey } from '@shared/workflow/formatAttributeKey.js';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';
import { getDeep } from '@shared/utils/getDeep.js';
import { workflowStepOutput } from '@/redux/selectors/workflowSelectors.js';
import { useSelector } from 'react-redux';
import { resolveIterateChain, drillDownNestedData } from '@/screens/workflows/lib/resolveNestedIterateData.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';

const hasParentWithFocus = (element) => {
  if (!element) {
    return false;
  }
  let parent = element;
  while (parent) {
    if (parent === document.activeElement) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
};

const BlockComponent = ({ data, nodeKey }) => {
  const stepAppId = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, data?.stepId)?.appId);
  const stepNumber = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, data?.stepId)?.stepNumber);
  const appId = useSelector((state) => selectors.appById(state, stepAppId)?.id);
  const appIcon = useSelector((state) => selectors.appById(state, stepAppId)?.icon);
  const appSlug = useSelector((state) => selectors.appById(state, stepAppId)?.slug);
  const stepOutputData = useSelector(memoizeSelector(`stepOutputData-${data?.stepId}`, (state) => workflowStepOutput(state, data?.stepId)?.data)) || [];
  const stepOutputPage = useSelector((state) => workflowStepOutput(state, data?.stepId)?.meta?.output?.page) || 0;
  const stepOutputLimit = useSelector((state) => workflowStepOutput(state, data?.stepId)?.meta?.output?.limit) || 20;
  const activeIndex = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, data?.stepId)?.arguments?.activeIndex);
  const stepDataKey = useSelector((state) => workflowScreenSelectors.activeWorkflowStepById(state, data?.stepId)?.arguments?.dataKey);

  // For nested iterate resolution - only pull what's needed
  const workflowSteps = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.steps);
  const apps = useSelector(selectors.appsVersions);

  // Resolve iterate chain to find original source (only for iterate steps)
  const { chain, originalStepId } = useMemo(() => {
    if (appSlug !== 'iterate') {
      return { chain: [], originalStepId: null };
    }
    return resolveIterateChain(data?.stepId, workflowSteps, apps);
  }, [appSlug, data?.stepId, workflowSteps, apps]);

  // Pull only the original source step's output (not all outputs)
  const originalStepOutput = useArgSelector(workflowStepOutput, originalStepId);

  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [selection, setSelection] = useState(null);
  const isFocused = $isNodeSelection(selection) && isSelected;
  const ref = useRef(null);
  const editorIsFocused = hasParentWithFocus(ref.current);
  const isSpecialBlock = ['system', 'memory', 'webhook', 'functionInputs', 'workflowArguments']?.includes(data?.stepId);
  const isDeadReference = !data?.stepId && !appId && !isSpecialBlock;

  const output = useMemo(() => {
    // Handle iterate steps with nested data resolution
    if (appSlug === 'iterate' && originalStepOutput?.data) {
      const resolvedData = drillDownNestedData(originalStepOutput, chain);

      if (resolvedData) {
        const cleanKey = data.key.replace(/^input/, '').replace(/^\[(\d+)\]\.?/, '');
        return cleanKey ? getDeep(cleanKey, resolvedData) : resolvedData;
      }
      return undefined;
    }

    // Standard non-iterate handling
    let targetData = stepOutputData;
    let isIndexed = false;

    const pageOffset = stepOutputPage * stepOutputLimit;
    const localIndex = activeIndex !== undefined && activeIndex !== null ? activeIndex - pageOffset : undefined;
    const isIndexOnCurrentPage = localIndex !== undefined && localIndex >= 0 && localIndex < stepOutputData.length;

    if (Array.isArray(stepOutputData) && stepOutputData.length > 0) {
      if (stepDataKey && stepOutputData[0]?.[stepDataKey]) {
        const dataKeyArray = stepOutputData[0][stepDataKey];
        if (Array.isArray(dataKeyArray) && activeIndex !== undefined && activeIndex !== null) {
          targetData = dataKeyArray[activeIndex];
          isIndexed = true;
        }
      } else if (activeIndex !== undefined && activeIndex !== null) {
        if (isIndexOnCurrentPage) {
          targetData = stepOutputData[localIndex];
        } else {
          targetData = undefined;
        }
        isIndexed = true;
      }
    }

    let cleanKey = data.key.replace(/^input/, '');
    if (isIndexed) {
      cleanKey = cleanKey.replace(/^\[(\d+)\]\.?/, '');
    } else {
      cleanKey = cleanKey.replace(/^\[(\d+)\]/, '$1');
    }

    return getDeep(cleanKey, targetData);
  }, [stepOutputData, stepDataKey, activeIndex, stepOutputPage, stepOutputLimit, data.key, appSlug, originalStepOutput, chain]);

  const title = useMemo(() => {
    if (isDeadReference) {
      return 'The referenced step no longer exists.';
    }
    if (output) {
      return output;
    }
    return formatAttributeKey(data?.key);
  }, [data?.key, isDeadReference, output]);

  const displayLabel = useMemo(() => {
    const isEntireOutput = data?.key === 'input' || data?.key?.match(/^input\[\d+\]$/);
    if (isEntireOutput) {
      return '(Entire Output)';
    }
    return formatAttributeKey(data?.key);
  }, [data?.key]);

  const onDelete = useCallback(
    (event) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isBlockNode(node)) {
          node.remove();
        }
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        setSelection(editorState.read(() => $getSelection()));
      }),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const event = payload;
          if (ref.current && (ref.current === event.target || ref.current.contains(event.target))) {
            if (!event.shiftKey) {
              clearSelection();
            }
            setSelected(true);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
    );
  }, [clearSelection, editor, isSelected, nodeKey, onDelete, setSelected]);

  return (
    <div className={`lexical-block ${isFocused && editorIsFocused ? 'focused' : ''} ${isDeadReference ? 'invalid' : ''}`} ref={ref} data-title={title}>
      <span className={'label'}>{`${stepNumber || ''} ${displayLabel}`.trim()}</span>
      {data.map && <div className={'label map'}>{`MAP`}</div>}
      <IconV2 icon={appIcon || utilityAppIcons[appSlug || data?.stepId] || FiStar} />
    </div>
  );
};

export default BlockComponent;
